import React from "react";
import { inject, observer } from "mobx-react";
import Store from "../../stores/NewExistingOwnerStore";
import {
  FindViews,
  VehicleIdType,
  FormFieldState,
  UserStatus,
  PageViews,
  VehicleInfo,
} from "../../models/NewExistingOwner";
import { Disclaimer, OwnerNav, FormField, FieldTypes } from "./";
import { ContentRecallImagePath } from "../../utils/ContentMediaPath";
import CustomEvent from "custom-event";

enum InlineRegoType {
  UPDATE = "UPDATE",
  LOOKUP = "LOOKUP",
}

interface IProps {
  store?: Store;
}
interface IState {
  step: FindViews;
  registration: string;
  vin: string;
  isEditingRegistration: boolean;
}

@inject("store")
@observer
class FindCar extends React.Component<IProps, IState> {
  state: IState = {
    step: FindViews.Find,
    registration: "",
    vin: "",
    isEditingRegistration: false,
  };

  private vinLength: number = 17;

  private fieldRefs: any = {
    registration: null,
  };

  get store(): Store {
    return this.props.store;
  }

  private showVinModal(e?: any) {
    const content = {
      text: `<div>
          <h3>Where do I find my VIN? (Vehicle Identification Number)</h3>
          <p>
            The Vehicle Identification Number (VIN) can be found in a few places including on the the car's registration
            label, on the build plate in the engine bay or on the passenger side windshield and door jamb. It also
            appears on registration and insurance documents. Make sure you enter the VIN accurately, here are tips to
            make sure you have the right number:
          </p>
          <p>
            <img src="${ContentRecallImagePath}takataRecall/wheres-my-vin.jpg" loading="lazy"/>
          </p>
          <h3>VIN Accuracy Tips</h3>
          <ul>
            <li>The letters "I", "O", and "Q" never appear in a VIN</li>
            <li>The VIN must be exactly 17 characters in length.</li>
          </ul>
        </div>`,
    };

    if (e) {
      e.preventDefault();
    }

    const evt = new CustomEvent("showPopupModal", { detail: { content } });
    window.dispatchEvent(evt);
  }

  private registration() {
    const { registration, userStatus } = this.store;
    const { isEditingRegistration } = this.state;

    if (isEditingRegistration) {
      return (
        <div className="neo__editRego">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const isValid: boolean = this.fieldRefs.registration.isValid();
              if (!isValid) return;
              this.setState(
                { isEditingRegistration: false },
                () => (this.store.registration = this.state.registration)
              );
            }}
          >
            <FormField
              ref={(el) => (this.fieldRefs.registration = el)}
              className={"neo__editRegoField"}
              type={FieldTypes.Text}
              defaultValue={registration}
              label=""
              optional={false}
              errorMsg={"Required"}
              onChange={(res: FormFieldState) => {
                this.setState({ registration: res.value.toUpperCase() });
              }}
            />

            <input type="submit" className="btn btn--text" value={"Save"} />

            <button
              className="btn btn--text"
              onClick={() => {
                this.setState({ isEditingRegistration: false, registration });
              }}
            >
              Cancel
            </button>
          </form>
        </div>
      );
    } else {
      return (
        <p>
          <strong>{registration}</strong>{" "}
          {registration && userStatus === UserStatus.Existing ? (
            <a
              onClick={() =>
                this.setState({
                  isEditingRegistration: true,
                })
              }
            >
              Edit
            </a>
          ) : null}
        </p>
      );
    }
  }

  private handleFindVehicle(type?: VehicleIdType): void {
    const { registration, vin } = this.state;
    let id = type;

    if (!id) {
      id = vin === "" ? VehicleIdType.Registration : VehicleIdType.Vin;
    }

    this.store.fetchVehicleInfo(
      id,
      id === VehicleIdType.Registration ? registration : vin,
      (res: VehicleInfo) => {
        if (!res || !res.modelName) {
          this.setState({
            step: FindViews.NotFound,
            isEditingRegistration: false,
          });
        } else {
          this.store.registration = res.registration;
          this.store.vin = res.vin;
          this.store.modelName = res.modelName;
          this.store.year = res.year;
          this.store.variant = res.variant;
          this.store.warrantyEligible = res.warrantyEligible;

          this.setState({
            step: FindViews.Found,
            registration: res.registration,
            vin: res.vin,
            isEditingRegistration: false,
          });
        }
      }
    );
  }

  private fieldVin() {
    const { vin } = this.state;

    return (
      <div>
        <label htmlFor="">VIN</label>
        <input
          type="text"
          id=""
          name=""
          className="text"
          value={vin}
          maxLength={17}
          onChange={(e) => {
            this.setState({
              vin: this.cleanAlphaNumeric(e.target.value),
              registration: "",
            });
          }}
        />
        <div className="text-right">
          <a
            className="btn btn--text text-right"
            onClick={() => this.showVinModal()}
          >
            Where do I find my VIN?
          </a>
        </div>
      </div>
    );
  }

  private find() {
    const { registration, vin } = this.state;
    const enableSubmit: boolean =
      registration.length > 0 || vin.length === this.vinLength;

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          enableSubmit && this.handleFindVehicle();
        }}
      >
        <div className="neo__orGroup neo__orGroup--find">
          <div className="neo__orOpt">
            <div>
              <label htmlFor="">Registration number</label>
              <input
                type="text"
                id=""
                name=""
                className="text"
                value={registration}
                onChange={(e) => {
                  this.setState({
                    registration: this.formatRegistration(e.target.value),
                    vin: "",
                  });
                }}
              />
            </div>
          </div>
          <div className="neo__orSep" />
          <div className="neo__orOpt">{this.fieldVin()}</div>
        </div>

        <div className="text-center">
          <button
            className="btn btn--primary"
            disabled={!enableSubmit}
            type={"submit"}
          >
            Find my car
          </button>
        </div>

        <Disclaimer />
      </form>
    );
  }

  private findByVin() {
    const { vin } = this.state;
    const enableSubmit: boolean = vin.length === this.vinLength;

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          enableSubmit && this.handleFindVehicle();
        }}
      >
        <div className="neo__orGroup neo__orGroup--find">
          <div className="neo__orOpt" style={{ display: "none" }} />
          <div className="neo__orSep" style={{ display: "none" }} />
          <div className="neo__orOpt">{this.fieldVin()}</div>
        </div>

        <div className="text-center">
          <button
            className="btn btn--primary"
            disabled={!enableSubmit}
            type={"submit"}
          >
            Find my car
          </button>
        </div>

        <Disclaimer />
      </form>
    );
  }

  private found() {
    const { vin, modelName, year, variant, userStatus } = this.store;

    return (
      <div className="neo__carFoundDetails row">
        <div className="col-xs-12 col-sm-10 col-md-12 col-sm-offset-1 col-md-offset-0">
          <div className="neo__carDetails">
            <div className="neo__carDetailsItem">
              <span>Registration number</span>
              {this.registration()}
            </div>
            <div className="neo__carDetailsItem">
              <span>VIN</span>
              <p>
                <strong>{vin}</strong>
              </p>
            </div>
          </div>

          <div className="neo__carDetailsItem">
            <span>My Subaru model</span>
            <p>
              <strong>{modelName}</strong>
            </p>
          </div>

          <div className="neo__carDetailsItem">
            <span>Model Year</span>
            <p>
              <strong>{year}</strong>
            </p>
          </div>

          <div className="neo__carDetailsItem">
            <span>Variant</span>
            <p>
              <strong>{variant}</strong>
            </p>
          </div>
        </div>

        <div className="text-center">
          <p>&nbsp;</p>
          <button
            className="btn btn--primary"
            onClick={() => (this.store.activePage = PageViews.Details)}
          >
            Confirm car details
          </button>
          <br />
          <br />
          <button
            className="btn btn--text"
            onClick={() => {
              this.setState({
                step: FindViews.NotMyCar,
                registration: "",
              });
            }}
          >
            {userStatus === UserStatus.New ? (
              <span>This is not my car</span>
            ) : (
              <span>My car details are wrong</span>
            )}
          </button>
        </div>
      </div>
    );
  }

  private notFound() {
    const { registration } = this.store;

    return (
      <div className="text-center">
        {registration ? (
          <div>
            Registration:&nbsp;&nbsp;<strong>{registration}</strong>
            <p />
          </div>
        ) : null}
        <p>
          We can't seem to find a car matching in our database. Please enter
          your VIN and try again, alternatively please contact us directly on{" "}
          <strong>1800 22 66 43</strong>.
        </p>
        <p>&nbsp;</p>
        {this.findByVin()}
      </div>
    );
  }

  private notMyCar() {
    const { registration } = this.store;

    return (
      <div className="text-center">
        <p>
          Please enter your VIN and try again, alternatively please contact us
          directly on <strong>1800 22 66 43</strong>.
        </p>
        <p>&nbsp;</p>
        {this.findByVin()}
      </div>
    );
  }

  private cleanAlphaNumeric(str: string): string {
    return str.replace(/[^a-zA-Z0-9]/g, "");
  }

  private formatRegistration(str: string): string {
    return this.cleanAlphaNumeric(str).substring(0, 7).toUpperCase();
  }

  render() {
    const { step } = this.state;
    const { userStatus } = this.store;

    return (
      <section>
        <OwnerNav active={0} />
        <div className="container pageSection__content">
          <div className="row">
            <div className={this.store.innerWidthClassNames}>
              <div className="text-center">
                <h1 className="h1">
                  {userStatus === UserStatus.New
                    ? "register your Subaru"
                    : "update your details"}
                </h1>
                {step === FindViews.Find && userStatus === UserStatus.New ? (
                  <p>
                    If you have recently purchased a used Subaru, you can let us
                    know your contact information here.
                    <br />
                    <br />
                  </p>
                ) : null}

                {step === FindViews.Find &&
                userStatus === UserStatus.Existing ? (
                  <p>
                    If you have purchased a new Subaru from us previously, you
                    can update your details here.
                    <br />
                    <br />
                  </p>
                ) : null}

                <h2 className="h2">1. My car</h2>
                {step === FindViews.Find && (
                  <p>
                    Enter your registration number or your VIN to find your car.
                  </p>
                )}

                {step === FindViews.Found && (
                  <p>
                    We found the following vehicle details based on the
                    registration or VIN you entered.
                  </p>
                )}
                <p>&nbsp;</p>
              </div>
            </div>
          </div>

          {step === FindViews.Find && this.find()}
          {/* {step === FindViews.FindByVin && this.findByVin()} */}
          <div className="row">
            <div className={this.store.innerWidthClassNames}>
              {step === FindViews.Found && this.found()}
              {step === FindViews.NotFound && this.notFound()}
              {step === FindViews.NotMyCar && this.notMyCar()}
            </div>
          </div>
        </div>
      </section>
    );
  }
}

export default FindCar;
